<template>
  <div class="weather-exception-tracker d-flex flex-column">
    <!-- 
      Weather Exception Tracker Component
      
      Session/Auth: Uses window.vcCommunicator for authentication state.
      - sessionId and sessionState are obtained from vcCommunicator on mount
      - The 'createsession' and 'terminatesession' events update auth state
      - hasSession computed property gates all API operations
    -->

    <!-- Sign-in Required Banner -->
    <div v-if="!hasSession" class="alert alert-warning d-flex align-items-center mb-3">
      <i class="bi bi-lock me-2 fs-4"></i>
      <div class="flex-grow-1">
        <strong>Sign in required</strong>
        <p class="mb-0 small">Please sign in to use the Weather Exception Tracker. Your API key is needed to fetch weather data.</p>
      </div>
      <a class="btn btn-primary btn-sm" href="#" onclick="window.dialogManager?.showSignIn(); return false;">
        <i class="bi bi-box-arrow-in-right me-1"></i>Sign In
      </a>
    </div>

    <!-- Main Content -->
    <template v-else>
      <!-- Header Row -->
      <div class="row align-items-center mb-3">
        <div class="col">
          <h5 class="mb-0">
            <i class="bi bi-exclamation-diamond me-2"></i>Weather Exception Tracker
          </h5>
        </div>
        <div class="col-auto">
          <div class="btn-group btn-group-sm">
            <button class="btn btn-outline-secondary" @click="showImportExportModal = true" title="Import/Export Config">
              <i class="bi bi-gear"></i>
            </button>
            <button class="btn btn-outline-secondary" @click="clearCache" title="Clear Cache">
              <i class="bi bi-arrow-clockwise"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Top Control Bar: All controls on one line -->
      <div class="d-flex flex-wrap align-items-center gap-3 mb-3 p-2 bg-light rounded border">
        <!-- Criteria Toggle -->
        <a 
          href="#" 
          class="text-decoration-none d-flex align-items-center"
          @click.prevent="toggleCriteriaExpanded"
          title="Toggle criteria editor"
        >
          <i class="bi me-1" :class="criteriaExpanded ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
          <i class="bi bi-funnel me-1"></i>
          <span class="fw-medium">Criteria</span>
          <span v-if="activeRuleset?.criteria?.length" class="badge bg-primary ms-1">{{ activeRuleset.criteria.length }}</span>
        </a>

        <span class="text-muted">|</span>

        <!-- Locations Toggle -->
        <a 
          href="#" 
          class="text-decoration-none d-flex align-items-center"
          @click.prevent="locationsExpanded = !locationsExpanded"
          title="Toggle locations editor"
        >
          <i class="bi me-1" :class="locationsExpanded ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
          <i class="bi bi-geo-alt me-1"></i>
          <span class="fw-medium">Locations</span>
          <span class="badge bg-primary ms-1">{{ locations.length }}</span>
        </a>

        <span class="text-muted">|</span>

        <!-- Date Range -->
        <div class="d-flex align-items-center">
          <i class="bi bi-calendar3 me-1"></i>
          <DateRangeChooser 
            :queryDefinition="dateRangeQueryDefn"
            @change:daterange="handleDateRangeChange"
            linkClass="btn btn-outline-primary btn-sm py-0"
          />
        </div>

        <span class="text-muted">|</span>

        <!-- Units Toggle -->
        <div class="btn-group btn-group-sm">
          <button 
            class="btn" 
            :class="unitGroup === 'metric' ? 'btn-primary' : 'btn-outline-primary'"
            @click="setUnitGroup('metric')"
          >
            Metric
          </button>
          <button 
            class="btn" 
            :class="unitGroup === 'us' ? 'btn-primary' : 'btn-outline-primary'"
            @click="setUnitGroup('us')"
          >
            US
          </button>
        </div>

        <span class="text-muted">|</span>

        <!-- View Mode Toggle (hide grid on errors tab) -->
        <div v-if="filter !== 'errors'" class="d-flex align-items-center">
          <span class="small text-muted me-1">View:</span>
          <div class="btn-group btn-group-sm">
            <button 
              class="btn" 
              :class="viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'"
              @click="setViewMode('list')"
              title="List View"
            >
              <i class="bi bi-list-ul"></i>
            </button>
            <button 
              class="btn" 
              :class="viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'"
              @click="setViewMode('grid')"
              title="Grid View"
            >
              <i class="bi bi-grid-3x3"></i>
            </button>
          </div>
        </div>

        <!-- Run Check Button (pushed to right) -->
        <div class="ms-auto">
          <button 
            class="btn btn-sm btn-success" 
            :disabled="!canRun || isRunning"
            @click="runExceptionCheck"
          >
            <span v-if="isRunning">
              <span class="spinner-border spinner-border-sm me-1"></span>
              Running...
            </span>
            <span v-else>
              <i class="bi me-1 bi-play-fill"></i>
              Run Check
            </span>
          </button>
        </div>
      </div>

      <!-- Criteria Panel (collapsible) -->
      <div class="card mb-3" v-show="criteriaExpanded">
        <div class="card-body">
          <!-- Criteria Builder -->
          <div class="row">
            <div class="col-12">
              <div class="d-flex align-items-center mb-2">
                <label class="form-label small mb-0 me-2">Exception Criteria</label>
                <div class="dropdown">
                  <button class="btn btn-outline-secondary btn-sm dropdown-toggle" type="button" @click="showPresetsDropdown = !showPresetsDropdown">
                    <i class="bi bi-lightning me-1"></i>Presets
                  </button>
                  <ul class="dropdown-menu" :class="{ show: showPresetsDropdown }">
                    <li v-for="preset in presetsList" :key="preset.id">
                      <a class="dropdown-item" href="#" @click.prevent="applyPreset(preset)">
                        <i :class="preset.icon + ' me-2'"></i>{{ preset.name }}
                        <small class="d-block text-muted">{{ preset.description }}</small>
                      </a>
                    </li>
                  </ul>
                </div>
                <span class="ms-auto small text-muted">
                  Combine with:
                  <button 
                    class="btn btn-link btn-sm p-0 ms-1" 
                    :class="{ 'fw-bold': activeRuleset?.combineMode === 'OR' }"
                    @click="setCombineMode('OR')"
                  >OR</button>
                  /
                  <button 
                    class="btn btn-link btn-sm p-0" 
                    :class="{ 'fw-bold': activeRuleset?.combineMode === 'AND' }"
                    @click="setCombineMode('AND')"
                  >AND</button>
                </span>
              </div>

              <!-- Criteria List -->
              <div v-if="!activeRuleset || activeRuleset.criteria.length === 0" class="text-muted small p-2 border rounded bg-light mb-2">
                <i class="bi bi-info-circle me-1"></i>
                No criteria defined. Select a preset or add custom criteria to get started.
              </div>
              <div v-else class="criteria-list">
                <div 
                  v-for="(criterion, index) in activeRuleset.criteria" 
                  :key="criterion.id"
                  class="d-flex align-items-center gap-2 mb-2 p-2 border rounded bg-light"
                >
                  <div class="form-check form-switch mb-0">
                    <input 
                      type="checkbox" 
                      class="form-check-input" 
                      :id="'criterion-' + criterion.id"
                      v-model="criterion.enabled"
                    >
                  </div>

                  <!-- Criterion Name -->
                  <input 
                    type="text" 
                    class="form-control form-control-sm" 
                    style="width: 120px;"
                    v-model="criterion.name"
                    placeholder="Name"
                    title="Criterion name (optional)"
                  >
                  
                  <select class="form-select form-select-sm" style="width: auto;" v-model="criterion.field" @change="onCriterionFieldChange(criterion)">
                    <option v-for="field in availableFields" :key="field.id" :value="field.id">
                      {{ field.label }}
                    </option>
                  </select>

                  <select class="form-select form-select-sm" style="width: auto;" v-model="criterion.operator">
                    <option v-for="op in getOperatorsForCriterion(criterion)" :key="op.id" :value="op.id">
                      {{ op.label }}
                    </option>
                  </select>

                  <!-- Value input based on operator (hidden for display-only) -->
                  <template v-if="criterion.operator !== 'display'">
                    <template v-if="criterion.operator === 'between'">
                      <input 
                        type="number" 
                        class="form-control form-control-sm" 
                        style="width: 80px;"
                        v-model.number="criterion.value[0]"
                        placeholder="Min"
                      >
                      <span>to</span>
                      <input 
                        type="number" 
                        class="form-control form-control-sm" 
                        style="width: 80px;"
                        v-model.number="criterion.value[1]"
                        placeholder="Max"
                      >
                    </template>
                    <template v-else-if="criterion.operator !== 'present' && criterion.operator !== 'notpresent'">
                      <input 
                        v-if="getFieldType(criterion.field) === 'numeric'"
                        type="number" 
                        class="form-control form-control-sm" 
                        style="width: 100px;"
                        v-model.number="criterion.value"
                      >
                      <input 
                        v-else
                        type="text" 
                        class="form-control form-control-sm" 
                        style="width: 150px;"
                        v-model="criterion.value"
                        placeholder="Text to match"
                      >
                    </template>

                    <span class="small text-muted">{{ getFieldUnit(criterion.field) }}</span>

                    <select 
                      class="form-select form-select-sm" 
                      style="width: auto;" 
                      v-model="criterion.section" 
                      title="Apply to"
                      :disabled="isFieldSectionLocked(criterion.field)"
                    >
                      <option value="days" :disabled="!canFieldApplyTo(criterion.field, 'days')">Daily</option>
                      <option value="hours" :disabled="!canFieldApplyTo(criterion.field, 'hours')">Hourly</option>
                      <option value="both" :disabled="!canFieldApplyTo(criterion.field, 'both')">Both</option>
                    </select>

                    <!-- Color picker with inline color swatches (only for non-display operators) -->
                    <div class="color-picker-dropdown position-relative">
                      <button 
                        type="button"
                        class="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                        @click.stop="toggleColorPicker(criterion.id)"
                        title="Exception color"
                      >
                        <span 
                          class="d-inline-block rounded-circle" 
                          :style="{ 
                            width: '14px', 
                            height: '14px', 
                            backgroundColor: criterion.color ? getColorHex(criterion.color) : '#6c757d',
                            border: '1px solid rgba(0,0,0,0.2)'
                          }"
                        ></span>
                        <i class="bi bi-chevron-down" style="font-size: 0.6em;"></i>
                      </button>
                      <div 
                        v-if="openColorPickerId === criterion.id" 
                        class="color-picker-menu position-absolute bg-white border rounded shadow-sm p-1"
                        style="z-index: 1050; top: 100%; right: 0; min-width: 120px;"
                      >
                        <div 
                          class="color-option d-flex align-items-center gap-2 px-2 py-1 rounded cursor-pointer"
                          :class="{ 'bg-primary text-white': !criterion.color }"
                          @click="selectColor(criterion, null)"
                        >
                          <span 
                            class="d-inline-block rounded-circle flex-shrink-0" 
                            style="width: 16px; height: 16px; background-color: #6c757d; border: 1px solid rgba(0,0,0,0.2);"
                          ></span>
                          <span class="small">Default</span>
                        </div>
                        <div 
                          v-for="color in availableColors" 
                          :key="color.id"
                          class="color-option d-flex align-items-center gap-2 px-2 py-1 rounded cursor-pointer"
                          :class="{ 'bg-primary text-white': criterion.color === color.id }"
                          @click="selectColor(criterion, color.id)"
                        >
                          <span 
                            class="d-inline-block rounded-circle flex-shrink-0" 
                            :style="{ width: '16px', height: '16px', backgroundColor: color.hex, border: '1px solid rgba(0,0,0,0.2)' }"
                          ></span>
                          <span class="small">{{ color.label }}</span>
                        </div>
                      </div>
                    </div>
                  </template>

                  <button class="btn btn-outline-danger btn-sm" @click="removeCriterion(index)" title="Remove">
                    <i class="bi bi-x"></i>
                  </button>
                </div>
              </div>

              <button class="btn btn-outline-primary btn-sm" @click="addCriterion">
                <i class="bi bi-plus me-1"></i>Add Criterion
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Locations Panel (collapsible) -->
      <div class="card mb-3" v-show="locationsExpanded">
        <div class="card-body">
          <!-- 
            LocationsEditor Integration:
            - Reuses existing component for location management
            - Supports manual entry, CSV import, clipboard paste
            - Emits locationsChanged with updated locations array
          -->
          <LocationsEditor 
            :parentLocations="locations"
            :subscriptionParams="sessionState?.subscriptionParams"
            @locationsChanged="handleLocationsChanged"
          />
        </div>
      </div>

      <!-- Setup Required Messages -->
      <template v-if="locations.length === 0 || !activeRuleset?.criteria?.length">
        <div class="card">
          <div class="card-body text-center py-5">
            <i class="bi bi-info-circle text-muted mb-3" style="font-size: 2rem;"></i>
            <div v-if="locations.length === 0 && (!activeRuleset?.criteria?.length)">
              <p class="text-muted mb-2">Add locations and exception criteria to get started.</p>
              <p>
                <a href="#" @click.prevent="locationsExpanded = true" class="me-3">
                  <i class="bi bi-geo-alt me-1"></i>Add Locations
                </a>
                <a href="#" @click.prevent="criteriaExpanded = true">
                  <i class="bi bi-funnel me-1"></i>Add Criteria
                </a>
              </p>
            </div>
            <div v-else-if="locations.length === 0">
              <p class="text-muted mb-2">No locations added yet.</p>
              <a href="#" @click.prevent="locationsExpanded = true">
                <i class="bi bi-geo-alt me-1"></i>Add Locations
              </a>
            </div>
            <div v-else>
              <p class="text-muted mb-2">No exception criteria defined.</p>
              <a href="#" @click.prevent="criteriaExpanded = true">
                <i class="bi bi-funnel me-1"></i>Add Criteria
              </a>
            </div>
          </div>
        </div>
      </template>

      <!-- Ready to Run Message -->
      <template v-else-if="!hasRun && !isRunning">
        <div class="card">
          <div class="card-body text-center py-5">
            <i class="bi bi-play-circle text-success mb-3" style="font-size: 2rem;"></i>
            <p class="text-muted mb-2">Ready to check {{ locations.length }} location{{ locations.length !== 1 ? 's' : '' }} against {{ activeRuleset.criteria.length }} criteria.</p>
            <a href="#" @click.prevent="runExceptionCheck" class="btn btn-success">
              <i class="bi bi-play-fill me-1"></i>Run Check
            </a>
          </div>
        </div>
      </template>

      <!-- Results Area (only show when we have results or running) -->
      <template v-else>
        <!-- Filter Toggle & Grid Options -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="btn-group btn-group-sm">
            <button 
              class="btn" 
              :class="filter === 'all' ? 'btn-secondary' : 'btn-outline-secondary'"
              @click="setFilter('all')"
            >
              All <span class="badge" :class="filter === 'all' ? 'bg-light text-dark' : 'bg-secondary'">{{ locationResults.length }}</span>
            </button>
            <button 
              class="btn" 
              :class="filter === 'exceptions' ? 'btn-warning' : 'btn-outline-warning'"
              @click="setFilter('exceptions')"
            >
              <i class="bi bi-exclamation-triangle me-1"></i>
              Flagged <span class="badge" :class="filter === 'exceptions' ? 'bg-dark' : 'bg-warning text-dark'">{{ exceptionCount }}</span>
            </button>
            <button 
              v-if="errorCount > 0"
              class="btn" 
              :class="filter === 'errors' ? 'btn-danger' : 'btn-outline-danger'"
              @click="setFilter('errors')"
            >
              <i class="bi bi-x-circle me-1"></i>
              Errors <span class="badge" :class="filter === 'errors' ? 'bg-light text-dark' : 'bg-danger'">{{ errorCount }}</span>
            </button>
          </div>

          <!-- Grid Group By Toggle (only in grid view) -->
          <div v-if="viewMode === 'grid'" class="d-flex align-items-center">
            <span class="small text-muted me-2">Group by:</span>
            <div class="btn-group btn-group-sm">
              <button 
                class="btn" 
                :class="gridGroupBy === 'criteria' ? 'btn-primary' : 'btn-outline-primary'"
                @click="gridGroupBy = 'criteria'"
                title="Group by criteria (element type)"
              >
                <i class="bi bi-funnel me-1"></i>Criteria
              </button>
              <button 
                class="btn" 
                :class="gridGroupBy === 'location' ? 'btn-primary' : 'btn-outline-primary'"
                @click="gridGroupBy = 'location'"
                title="Group by location"
              >
                <i class="bi bi-geo-alt me-1"></i>Location
              </button>
            </div>
          </div>
        </div>

        <!-- Progress Bar (during run) -->
        <div v-if="isRunning" class="mb-3">
          <div class="d-flex justify-content-between small mb-1">
            <span>Processing locations...</span>
            <span>{{ completedCount }} / {{ totalToProcess }}</span>
          </div>
          <div class="progress" style="height: 4px;">
            <div 
              class="progress-bar" 
              role="progressbar" 
              :style="{ width: progressPercent + '%' }"
            ></div>
          </div>
        </div>

        <!-- List View -->
        <div v-if="viewMode === 'list'" class="table-responsive">
          <table class="table table-sm table-hover">
            <thead class="table-light">
              <tr>
              <th>Name</th>
              <th>Location</th>
              <th style="width: 100px;">Status</th>
              <th>Next Exception</th>
              <th style="width: 80px;">Exception Days</th>
              <th>Triggered Criteria</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="pagedResults.length === 0">
              <td colspan="6" class="text-center text-muted py-4">
                <template v-if="isRunning">
                  <span class="spinner-border spinner-border-sm me-2"></span>
                  Loading results...
                </template>
                <template v-else>
                  No results match the current filter.
                </template>
              </td>
            </tr>
            <tr 
              v-for="result in pagedResults" 
              :key="result.id"
              :class="getRowClass(result)"
              style="cursor: pointer;"
              @click="showLocationDetail(result)"
            >
              <td>
                <span class="fw-medium">{{ result.name || '-' }}</span>
                <span v-if="result.cached" class="badge bg-light text-muted ms-1" title="From cache">
                  <i class="bi bi-database"></i>
                </span>
              </td>
              <td class="text-truncate" style="max-width: 200px;" :title="result.location">
                {{ result.location }}
              </td>
              <td>
                <!-- Status indicators:
                     - Pending: grey spinner
                     - OK (no matches): green checkmark
                     - OK (with matches): warning/orange with exclamation
                     - Error: red X
                -->
                <span v-if="result.status === 'pending'" class="badge bg-secondary">
                  <span class="spinner-border spinner-border-sm"></span>
                </span>
                <span v-else-if="result.status === 'error'" class="badge bg-danger" :title="result.errorMessage">
                  <i class="bi bi-x-circle"></i> Error
                </span>
                <span v-else-if="result.status === 'ok' && result.exceptionCount > 0" class="badge bg-warning text-dark">
                  <i class="bi bi-exclamation-triangle"></i> Flagged
                </span>
                <span v-else-if="result.status === 'ok'" class="badge bg-success">
                  <i class="bi bi-check-circle"></i> OK
                </span>
              </td>
              <td>
                <span v-if="result.nextExceptionDate" class="text-warning fw-medium">
                  {{ result.nextExceptionDate }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>
              <td>
                <span v-if="result.exceptionCount > 0" class="badge bg-warning text-dark">
                  {{ result.exceptionCount }}
                </span>
                <span v-else class="text-muted">0</span>
              </td>
              <td>
                <div class="d-flex flex-wrap gap-1">
                  <span 
                    v-for="criterionId in result.triggeredCriteria?.slice(0, 3)" 
                    :key="criterionId"
                    class="badge bg-warning text-dark"
                    :title="getCriterionDescription(criterionId)"
                  >
                    {{ getCriterionLabel(criterionId) }}
                  </span>
                  <span v-if="(result.triggeredCriteria?.length || 0) > 3" class="badge bg-secondary">
                    +{{ result.triggeredCriteria.length - 3 }}
                  </span>
                  <span v-if="!result.triggeredCriteria || result.triggeredCriteria.length === 0" class="text-muted">-</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination (List View) -->
      <nav v-if="viewMode === 'list' && totalPages > 1" aria-label="Results pagination">
        <ul class="pagination pagination-sm justify-content-center">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a class="page-link" href="#" @click.prevent="goToPage(1)">
              <i class="bi bi-chevron-double-left"></i>
            </a>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">
              <i class="bi bi-chevron-left"></i>
            </a>
          </li>
          <li 
            v-for="page in visiblePages" 
            :key="page"
            class="page-item" 
            :class="{ active: page === currentPage }"
          >
            <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">
              <i class="bi bi-chevron-right"></i>
            </a>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <a class="page-link" href="#" @click.prevent="goToPage(totalPages)">
              <i class="bi bi-chevron-double-right"></i>
            </a>
          </li>
        </ul>
      </nav>

      <!-- Grid View -->
      <div v-if="viewMode === 'grid'" class="grid-view">
        <!-- Empty state -->
        <div v-if="gridElements.length === 0" class="text-center text-muted py-4">
          No criteria elements to display.
        </div>
        
        <!-- Grid grouped by CRITERIA (default) -->
        <template v-else-if="gridGroupBy === 'criteria'">
          <template v-for="(elementData, elementId) in gridData" :key="elementId">
            <div class="card mb-3">
              <div class="card-header py-2 bg-light d-flex justify-content-between align-items-center">
                <span class="fw-medium">
                  <template v-if="elementData.criteriaNames?.length">
                    {{ elementData.criteriaNames.join(', ') }}
                    <span class="text-muted">—</span>
                  </template>
                  {{ elementData.label }}
                  <span class="text-muted small ms-1">({{ elementData.unit }})</span>
                  <span v-if="elementData.isStubOnly" class="badge bg-secondary ms-1" title="Display only - no exceptions triggered">display</span>
                  <span 
                    v-for="color in elementData.colors" 
                    :key="color"
                    class="badge rounded-circle p-1 ms-1" 
                    :style="{ backgroundColor: getColorHex(color) }"
                  >&nbsp;</span>
                </span>
                <span class="badge" :class="elementData.section === 'hours' ? 'bg-info' : 'bg-secondary'">
                  {{ elementData.section === 'hours' ? 'Hourly' : 'Daily' }}
                </span>
              </div>
              <div class="card-body p-0">
                <div class="table-responsive">
                  <table class="table table-sm table-bordered mb-0" style="font-size: 0.85rem;">
                    <thead class="table-light">
                      <tr>
                        <th class="sticky-left bg-light" style="min-width: 150px; position: sticky; left: 0; z-index: 1;">Location</th>
                        <th 
                          v-for="date in gridDates" 
                          :key="date" 
                          class="text-center" 
                          style="min-width: 70px;"
                        >
                          {{ formatGridDate(date) }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="elementData.locations.length === 0">
                        <td :colspan="gridDates.length + 1" class="text-center text-muted py-3">
                          No data available
                        </td>
                      </tr>
                      <tr v-for="loc in elementData.locations" :key="loc.location">
                        <td class="sticky-left bg-white" style="position: sticky; left: 0; z-index: 1; border-right: 2px solid #dee2e6;">
                          <span class="fw-medium">{{ loc.name }}</span>
                        </td>
                        <td 
                          v-for="date in gridDates" 
                          :key="date"
                          class="text-center"
                          :class="getCellClass(loc.values[date])"
                          :style="getCellStyle(loc.values[date])"
                          @click="loc.values[date]?.isHourly && loc.values[date]?.hourlyExceptionCount > 0 ? openGridCellDetail(loc.location, date) : null"
                          :title="loc.values[date]?.isHourly && loc.values[date]?.hourlyExceptionCount > 0 ? 'Click to view hourly details' : ''"
                        >
                          <!-- Daily criteria: show value -->
                          <template v-if="!loc.values[date]?.isHourly">
                            <span v-if="loc.values[date]?.value !== undefined && loc.values[date]?.value !== null">
                              {{ formatGridValue(loc.values[date].value, elementId) }}
                            </span>
                            <span v-else class="text-muted">-</span>
                          </template>
                          <!-- Hourly criteria: show daily value + exception hour count -->
                          <template v-else>
                            <template v-if="loc.values[date]?.value !== undefined && loc.values[date]?.value !== null">
                              {{ formatGridValue(loc.values[date].value, elementId) }}
                              <small 
                                v-if="loc.values[date]?.hourlyExceptionCount > 0" 
                                class="d-block" 
                                style="font-size: 0.7em;"
                              >
                                <span class="badge" :class="getColorClass(loc.values[date]?.color)">{{ loc.values[date].hourlyExceptionCount }}h</span>
                              </small>
                            </template>
                            <template v-else-if="loc.values[date]?.hourlyExceptionCount > 0">
                              <span class="badge" :class="getColorClass(loc.values[date]?.color)">{{ loc.values[date].hourlyExceptionCount }}h</span>
                            </template>
                            <span v-else class="text-muted">-</span>
                          </template>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </template>
        </template>

        <!-- Grid grouped by LOCATION -->
        <template v-else-if="gridGroupBy === 'location'">
          <template v-for="(locData, locKey) in gridDataByLocation" :key="locKey">
            <div class="card mb-3">
              <div class="card-header py-2 bg-light d-flex justify-content-between align-items-center">
                <span class="fw-medium">
                  <i class="bi bi-geo-alt me-1"></i>
                  {{ locData.name }}
                </span>
                <span class="text-muted small">{{ locData.location }}</span>
              </div>
              <div class="card-body p-0">
                <div class="table-responsive">
                  <table class="table table-sm table-bordered mb-0" style="font-size: 0.85rem;">
                    <thead class="table-light">
                      <tr>
                        <th class="sticky-left bg-light" style="min-width: 150px; position: sticky; left: 0; z-index: 1;">Criteria</th>
                        <th 
                          v-for="date in gridDates" 
                          :key="date" 
                          class="text-center" 
                          style="min-width: 70px;"
                        >
                          {{ formatGridDate(date) }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(elemData, elemId) in locData.elements" :key="elemId">
                        <td class="sticky-left bg-white" style="position: sticky; left: 0; z-index: 1; border-right: 2px solid #dee2e6;">
                          <span class="fw-medium">
                            <template v-if="elemData.criteriaNames?.length">
                              {{ elemData.criteriaNames.join(', ') }}
                            </template>
                            <template v-else>{{ elemData.label }}</template>
                          </span>
                          <span class="text-muted small ms-1">({{ elemData.unit }})</span>
                          <span v-if="elemData.isStubOnly" class="badge bg-secondary ms-1" style="font-size: 0.6em;">display</span>
                        </td>
                        <td 
                          v-for="date in gridDates" 
                          :key="date"
                          class="text-center"
                          :class="getCellClass(elemData.values[date])"
                          :style="getCellStyle(elemData.values[date])"
                          @click="elemData.values[date]?.isHourly && elemData.values[date]?.hourlyExceptionCount > 0 ? openGridCellDetail(locKey, date) : null"
                          :title="elemData.values[date]?.isHourly && elemData.values[date]?.hourlyExceptionCount > 0 ? 'Click to view hourly details' : ''"
                        >
                          <template v-if="!elemData.values[date]?.isHourly">
                            <span v-if="elemData.values[date]?.value !== undefined && elemData.values[date]?.value !== null">
                              {{ formatGridValue(elemData.values[date].value, elemId) }}
                            </span>
                            <span v-else class="text-muted">-</span>
                          </template>
                          <template v-else>
                            <template v-if="elemData.values[date]?.value !== undefined && elemData.values[date]?.value !== null">
                              {{ formatGridValue(elemData.values[date].value, elemId) }}
                              <small v-if="elemData.values[date]?.hourlyExceptionCount > 0" class="d-block" style="font-size: 0.7em;">
                                <span class="badge" :class="getColorClass(elemData.values[date]?.color)">{{ elemData.values[date].hourlyExceptionCount }}h</span>
                              </small>
                            </template>
                            <template v-else-if="elemData.values[date]?.hourlyExceptionCount > 0">
                              <span class="badge" :class="getColorClass(elemData.values[date]?.color)">{{ elemData.values[date].hourlyExceptionCount }}h</span>
                            </template>
                            <span v-else class="text-muted">-</span>
                          </template>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </template>
        </template>
      </div>

      <!-- Pagination (Grid View) -->
      <nav v-if="viewMode === 'grid' && totalPages > 1" aria-label="Results pagination" class="mt-3">
        <ul class="pagination pagination-sm justify-content-center">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a class="page-link" href="#" @click.prevent="goToPage(1)">
              <i class="bi bi-chevron-double-left"></i>
            </a>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">
              <i class="bi bi-chevron-left"></i>
            </a>
          </li>
          <li 
            v-for="page in visiblePages" 
            :key="page"
            class="page-item" 
            :class="{ active: page === currentPage }"
          >
            <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">
              <i class="bi bi-chevron-right"></i>
            </a>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <a class="page-link" href="#" @click.prevent="goToPage(totalPages)">
              <i class="bi bi-chevron-double-right"></i>
            </a>
          </li>
        </ul>
      </nav>
      </template>

    <!-- Location Detail Modal -->
    <div 
      v-if="selectedResult" 
      class="modal fade show" 
      style="display: block;" 
      tabindex="-1"
      @click.self="selectedResult = null"
      @keydown.esc="selectedResult = null"
    >
      <div class="modal-backdrop fade show" @click="selectedResult = null"></div>
      <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-geo-alt me-2"></i>
              {{ selectedResult.name || selectedResult.location }}
            </h5>
            <button type="button" class="btn-close" @click="selectedResult = null"></button>
          </div>
          <div class="modal-body">
            <!-- Error state -->
            <div v-if="selectedResult.status === 'error'" class="alert alert-danger">
              <i class="bi bi-exclamation-triangle me-2"></i>
              <strong>Error:</strong> {{ selectedResult.errorMessage }}
            </div>

            <!-- Location info -->
            <div v-else class="mb-3">
              <div class="row small mb-2">
                <div class="col-auto">
                  <strong>Location:</strong> {{ selectedResult.location }}
                </div>
                <div v-if="selectedResult.timezone" class="col-auto">
                  <strong>Timezone:</strong> {{ selectedResult.timezone }}
                </div>
                <div class="col-auto">
                  <strong>Exception Days:</strong> 
                  <span class="badge bg-warning text-dark">{{ selectedResult.exceptionCount }}</span>
                </div>
              </div>

              <!-- Days table -->
              <div class="table-responsive" style="max-height: 400px;">
                <table class="table table-sm table-bordered">
                  <thead class="table-light sticky-top">
                    <tr>
                      <th style="width: 30px;"></th>
                      <th>Date</th>
                      <th>Max</th>
                      <th>Min</th>
                      <th>Precip</th>
                      <th>Prob%</th>
                      <th>Wind Gust</th>
                      <th>Snow</th>
                      <th>Snow Depth</th>
                      <th>Alerts</th>
                      <th>Triggered Exceptions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="day in selectedResult.days" :key="day.datetime">
                      <!-- Day row -->
                      <tr :class="{ 'table-warning': day.isException }">
                        <td class="text-center">
                          <button 
                            v-if="day.hours && day.matchedHours.length > 0"
                            class="btn btn-link btn-sm p-0"
                            @click="day.isExpanded = !day.isExpanded"
                            :title="day.isExpanded ? 'Hide hours' : 'Show exception hours'"
                          >
                            <i :class="day.isExpanded ? 'bi bi-chevron-down' : 'bi bi-chevron-right'"></i>
                          </button>
                          <span v-else-if="day.hours && day.hours.length > 0" class="text-muted small">
                            <i class="bi bi-dot"></i>
                          </span>
                        </td>
                        <td class="fw-medium">
                          {{ day.datetime }}
                          <span v-if="day.matchedHours.length > 0" class="badge bg-secondary ms-1" :title="'Hours: ' + day.matchedHours.join(', ')">
                            {{ day.matchedHours.length }}h
                          </span>
                        </td>
                        <td>{{ formatValue(day.tempmax, 'temp') }}</td>
                        <td>{{ formatValue(day.tempmin, 'temp') }}</td>
                        <td>{{ formatValue(day.precip, 'precip') }}</td>
                        <td>{{ formatValue(day.precipprob, 'percent') }}</td>
                        <td>{{ formatValue(day.windgust, 'speed') }}</td>
                        <td>{{ formatValue(day.snow, 'snow') }}</td>
                        <td>{{ formatValue(day.snowdepth, 'snow') }}</td>
                        <td>
                          <span 
                            v-if="day.hasAlerts" 
                            class="badge bg-danger"
                            :title="formatAlertTooltip(day.activeAlerts, day.alertMatchedHours)"
                          >
                            <i class="bi bi-exclamation-triangle"></i>
                            {{ day.activeAlerts?.length || '' }}
                          </span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td>
                          <div v-if="day.matchedCriteria && day.matchedCriteria.length > 0" class="d-flex flex-wrap gap-1">
                            <span 
                              v-for="criterionId in day.matchedCriteria" 
                              :key="criterionId"
                              class="badge bg-warning text-dark small"
                              :title="getCriterionDescription(criterionId)"
                            >
                              {{ getCriterionLabel(criterionId) }}
                            </span>
                          </div>
                          <span v-else class="text-muted">-</span>
                        </td>
                      </tr>
                      <!-- Expanded hourly rows -->
                      <template v-if="day.isExpanded && day.hours">
                        <tr 
                          v-for="hour in day.hours" 
                          :key="day.datetime + '-' + hour.datetime"
                          :class="{ 'table-warning': day.matchedHours.includes(hour.datetime) }"
                          class="small"
                        >
                          <td></td>
                          <td class="ps-4 text-muted">
                            <i class="bi bi-clock me-1"></i>{{ hour.datetime }}
                          </td>
                          <td colspan="2">{{ formatValue(hour.temp, 'temp') }}</td>
                          <td>{{ formatValue(hour.precip, 'precip') }}</td>
                          <td>{{ formatValue(hour.precipprob, 'percent') }}</td>
                          <td>{{ formatValue(hour.windgust, 'speed') }}</td>
                          <td colspan="2">-</td>
                          <td>
                            <span v-if="day.alertMatchedHours.includes(hour.datetime)" class="badge bg-danger">
                              <i class="bi bi-exclamation-triangle"></i>
                            </span>
                          </td>
                          <td>
                            <div v-if="day.hourCriteriaMap && day.hourCriteriaMap[hour.datetime]" class="d-flex flex-wrap gap-1">
                              <span 
                                v-for="criterionId in day.hourCriteriaMap[hour.datetime]" 
                                :key="criterionId"
                                class="badge bg-warning text-dark small"
                                :title="getCriterionDescription(criterionId)"
                              >
                                {{ getCriterionLabel(criterionId) }}
                              </span>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="selectedResult = null">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Import/Export Modal -->
    <div 
      v-if="showImportExportModal" 
      class="modal fade show" 
      style="display: block;" 
      tabindex="-1"
      @click.self="showImportExportModal = false"
    >
      <div class="modal-backdrop fade show" @click="showImportExportModal = false"></div>
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-gear me-2"></i>Import / Export Configuration
            </h5>
            <button type="button" class="btn-close" @click="showImportExportModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="d-grid gap-3">
              <div>
                <h6>Export Current Configuration</h6>
                <p class="small text-muted">Download all settings, locations, and rulesets as a JSON file.</p>
                <button class="btn btn-outline-primary" @click="exportConfig">
                  <i class="bi bi-download me-1"></i>Export Config
                </button>
              </div>
              <hr>
              <div>
                <h6>Import Configuration</h6>
                <p class="small text-muted">Load settings from a previously exported JSON file. This will replace your current configuration.</p>
                <input 
                  type="file" 
                  ref="importFileInput"
                  class="form-control form-control-sm mb-2" 
                  accept=".json"
                  @change="handleImportFile"
                >
                <div v-if="importError" class="alert alert-danger small py-2">
                  {{ importError }}
                </div>
                <div v-if="importSuccess" class="alert alert-success small py-2">
                  Configuration imported successfully!
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showImportExportModal = false">Close</button>
          </div>
        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
/**
 * WeatherExceptionTracker Component
 * 
 * Allows users to define weather exception criteria and evaluate them
 * across multiple locations using the Visual Crossing Timeline API.
 * 
 * Key integrations:
 * - Session/Auth: window.vcCommunicator (see mounted hook)
 * - Locations: LocationsEditor component
 * - Date Range: DateRangeChooser component
 * - API: Direct fetch to Timeline API endpoint
 */

// Existing utilities from visualcrossing-core
import { VcDateUtils } from '/src/visualcrossing-core/vcdateutils';
import { getServerRoot } from './apiConfig.js';

// Reused components
import DateRangeChooser from '../DateRangeChooser.vue';
import LocationsEditor from '../LocationsEditor.vue';

// Exception tracking utilities
import {
  PRESETS,
  WEATHER_FIELDS,
  OPERATORS,
  FIELD_TYPES,
  CRITERION_COLORS,
  createCriterion,
  createRuleset,
  getOperatorsForField,
  evaluateRulesetForLocation,
  getActiveAlertsForDay,
  cloneRuleset,
  convertRulesetUnits,
  convertAllRulesetsUnits
} from './exceptionCriteriaPresets.js';

import {
  generateCacheKey,
  getCacheEntry,
  setCacheEntry,
  clearAllCache,
  loadSettings,
  saveSettings,
  exportSettings,
  importSettingsFromFile
} from './exceptionCachePersistence.js';

export default {
  name: 'WeatherExceptionTracker',

  components: {
    DateRangeChooser,
    LocationsEditor
  },


  data() {
    return {
      // ---- Session/Auth State ----
      // These are populated from window.vcCommunicator on mount
      sessionId: null,
      sessionState: null,
      isContainerInited: false,

      // ---- Server Configuration ----
      serverRootOverride: null, // Override VcGlobal.SERVER_ROOT for this component

      // ---- Date Range State ----
      // Used by DateRangeChooser, stored as dynamic period strings or ISO dates
      startPeriod: 'next10days',
      endPeriod: null,

      // ---- Settings ----
      unitGroup: 'metric',  // 'us' or 'metric'
      filter: 'all',        // 'all', 'exceptions', 'errors'
      pageSize: 25,

      // ---- Locations ----
      locations: [],

      // ---- Criteria/Rulesets ----
      activeRuleset: null,
      savedRulesets: [],

      // ---- UI State ----
      showPresetsDropdown: false,
      showImportExportModal: false,
      selectedResult: null,
      importError: null,
      importSuccess: false,
      viewMode: 'list',  // 'list' or 'grid'
      savedViewMode: null,  // Saved view mode when switching to errors tab
      gridGroupBy: 'criteria',  // 'criteria' or 'location'
      criteriaExpanded: true,  // Criteria editor visibility
      locationsExpanded: false,  // Locations editor visibility
      openColorPickerId: null,  // ID of criterion with open color picker

      // ---- Run State ----
      isRunning: false,
      hasRun: false,
      locationResults: [],  // Array of result objects
      currentPage: 1,

      // ---- Request Queue ----
      requestQueue: [],
      activeRequests: 0,
      MAX_CONCURRENT_REQUESTS: 3
    };
  },

  computed: {
    /**
     * Check if user has an active session
     * Used to gate API operations
     */
    hasSession() {
      return !!this.sessionId && !!this.sessionState;
    },

    /**
     * Get the user's API key from session state
     */
    apiKey() {
      return this.sessionState?.apiKey || null;
    },

    /**
     * Get user ID for storage scoping
     * Uses stable userId from sessionState (email), not the JWT token which changes each session
     */
    userId() {
      // sessionState.userId contains the email, sessionId is the JWT token (changes each login)
      return this.sessionState?.userId || 'anonymous';
    },

    /**
     * Query definition object for DateRangeChooser
     */
    dateRangeQueryDefn() {
      return {
        startdate: this.startPeriod,
        enddate: this.endPeriod
      };
    },

    /**
     * Check if we can run the exception check
     */
    canRun() {
      return this.hasSession && 
             this.locations.length > 0 && 
             this.activeRuleset?.criteria?.length > 0;
    },

    /**
     * Derive granularity from criteria - returns 'days+hours' if any criterion requires hourly data
     * A criterion requires hourly data if:
     * 1. The field is hourly-only (e.g., 'temp' - section: 'hours')
     * 2. The criterion's section is set to 'hours' or 'both'
     */
    derivedGranularity() {
      if (!this.activeRuleset?.criteria?.length) return 'days';
      
      for (const criterion of this.activeRuleset.criteria) {
        const field = WEATHER_FIELDS[criterion.field];
        // If field is hourly-only, we need hourly data
        if (field?.section === 'hours') {
          return 'days+hours';
        }
        // If criterion is set to evaluate on hours or both, we need hourly data
        if (criterion.section === 'hours' || criterion.section === 'both') {
          return 'days+hours';
        }
      }
      return 'days';
    },

    /**
     * List of available presets for dropdown
     */
    presetsList() {
      return Object.values(PRESETS);
    },

    /**
     * List of available weather fields for criteria
     */
    availableFields() {
      return Object.values(WEATHER_FIELDS);
    },

    /**
     * List of available colors for criteria
     */
    availableColors() {
      return CRITERION_COLORS;
    },

    /**
     * Filtered results based on current filter
     */
    filteredResults() {
      if (this.filter === 'all') {
        return this.locationResults;
      } else if (this.filter === 'exceptions') {
        return this.locationResults.filter(r => r.exceptionCount > 0);
      } else if (this.filter === 'errors') {
        return this.locationResults.filter(r => r.status === 'error');
      }
      return this.locationResults;
    },

    /**
     * Paged results for current page
     */
    pagedResults() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredResults.slice(start, start + this.pageSize);
    },

    /**
     * Total number of pages
     */
    totalPages() {
      return Math.ceil(this.filteredResults.length / this.pageSize);
    },

    /**
     * Visible page numbers for pagination (always shows 5 pages when possible)
     */
    visiblePages() {
      const maxVisible = 5;
      const pages = [];
      
      if (this.totalPages <= maxVisible) {
        // Show all pages if total is less than max visible
        for (let i = 1; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Calculate start to keep current page centered when possible
        let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
        let end = start + maxVisible - 1;
        
        // Adjust if we're near the end
        if (end > this.totalPages) {
          end = this.totalPages;
          start = end - maxVisible + 1;
        }
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
      }
      
      return pages;
    },

    /**
     * Count of locations with exceptions
     */
    exceptionCount() {
      return this.locationResults.filter(r => r.exceptionCount > 0).length;
    },

    /**
     * Count of locations with errors
     */
    errorCount() {
      return this.locationResults.filter(r => r.status === 'error').length;
    },

    /**
     * Progress tracking
     */
    completedCount() {
      return this.locationResults.filter(r => r.status !== 'pending').length;
    },

    totalToProcess() {
      return this.locations.length;
    },

    progressPercent() {
      if (this.totalToProcess === 0) return 0;
      return Math.round((this.completedCount / this.totalToProcess) * 100);
    },

    /**
     * Get unique dates across all location results for grid view columns
     */
    gridDates() {
      const dateSet = new Set();
      for (const result of this.locationResults) {
        if (result.days) {
          for (const day of result.days) {
            dateSet.add(day.datetime);
          }
        }
      }
      return Array.from(dateSet).sort();
    },

    /**
     * Get elements to show in grid view (based on active criteria fields)
     * Includes stub criteria which display values but don't trigger exceptions
     */
    gridElements() {
      if (!this.activeRuleset?.criteria?.length) return [];
      
      // Get unique fields from criteria with their names and colors
      const fieldMap = new Map(); // fieldId -> { criteriaNames, usesHourly, colors, criteriaIds }
      for (const criterion of this.activeRuleset.criteria) {
        // enabled defaults to true if not set (for backwards compatibility)
        const isEnabled = criterion.enabled !== false;
        if (isEnabled && criterion.field !== 'alerts') {
          if (!fieldMap.has(criterion.field)) {
            fieldMap.set(criterion.field, { 
              criteriaNames: [], 
              usesHourly: false, 
              criteriaIds: [], 
              colors: [],
              hasNonStub: false 
            });
          }
          const entry = fieldMap.get(criterion.field);
          if (criterion.name) {
            entry.criteriaNames.push(criterion.name);
          }
          entry.criteriaIds.push(criterion.id);
          if (criterion.color) {
            entry.colors.push(criterion.color);
          }
          // Check if this criterion triggers exceptions (not a display-only)
          if (criterion.operator !== 'display') {
            entry.hasNonStub = true;
          }
          if (criterion.section === 'hours' || criterion.section === 'both') {
            entry.usesHourly = true;
          }
        }
      }
      
      return Array.from(fieldMap.entries()).map(([fieldId, info]) => {
        const field = WEATHER_FIELDS[fieldId];
        return {
          id: fieldId,
          label: field?.label || fieldId,
          section: info.usesHourly ? 'hours' : 'days',
          unit: field?.units?.[this.unitGroup] || '',
          criteriaNames: info.criteriaNames,
          criteriaIds: info.criteriaIds,
          colors: info.colors,
          isStubOnly: !info.hasNonStub // true if all criteria for this field are stubs
        };
      });
    },

    /**
     * Build grid data structure for rendering (grouped by criteria/element)
     * Structure: { elementId: { label, section, locations: [{ name, location, values: { date: { value, isException, color } } }] } }
     */
    gridData() {
      const data = {};
      
      for (const element of this.gridElements) {
        data[element.id] = {
          label: element.label,
          section: element.section,
          unit: element.unit,
          criteriaNames: element.criteriaNames,
          colors: element.colors,
          isStubOnly: element.isStubOnly,
          locations: []
        };
        
        // Use pagedResults to respect pagination
        for (const result of this.pagedResults) {
          if (result.status !== 'ok' || !result.days) continue;
          
          const locationData = {
            name: result.name || result.location,
            location: result.location,
            values: {}
          };
          
          for (const day of result.days) {
            // Check if this element triggered an exception for this day (ignore stubs)
            let isException = false;
            let matchedColor = null;
            
            if (!element.isStubOnly) {
              for (const cid of (day.matchedCriteria || [])) {
                const criterion = this.activeRuleset?.criteria?.find(c => c.id === cid);
                if (criterion?.field === element.id && !criterion.isStub) {
                  isException = true;
                  if (criterion.color) {
                    matchedColor = criterion.color;
                  }
                  break;
                }
              }
            }
            
            // Count hourly exceptions for this element on this day
            let hourlyExceptionCount = 0;
            if (element.section === 'hours' && day.hourCriteriaMap) {
              for (const hourCriteriaIds of Object.values(day.hourCriteriaMap)) {
                if (hourCriteriaIds?.some(cid => element.criteriaIds.includes(cid))) {
                  hourlyExceptionCount++;
                }
              }
            }
            
            locationData.values[day.datetime] = {
              value: day[element.id],
              isException,
              color: matchedColor,
              hourlyExceptionCount,
              isHourly: element.section === 'hours'
            };
          }
          
          data[element.id].locations.push(locationData);
        }
      }
      
      return data;
    },

    /**
     * Build grid data structure grouped by location
     * Structure: { locationId: { name, location, elements: { elementId: { label, values: { date: { value, isException, color } } } } } }
     */
    gridDataByLocation() {
      const data = {};
      
      // Use pagedResults to respect pagination
      for (const result of this.pagedResults) {
        if (result.status !== 'ok' || !result.days) continue;
        
        const locKey = result.location;
        data[locKey] = {
          name: result.name || result.location,
          location: result.location,
          elements: {}
        };
        
        // For each element/criteria field
        for (const element of this.gridElements) {
          data[locKey].elements[element.id] = {
            label: element.label,
            unit: element.unit,
            section: element.section,
            criteriaNames: element.criteriaNames,
            colors: element.colors,
            isStubOnly: element.isStubOnly,
            values: {}
          };
          
          for (const day of result.days) {
            // Check if this element triggered an exception
            let isException = false;
            let matchedColor = null;
            
            if (!element.isStubOnly) {
              for (const cid of (day.matchedCriteria || [])) {
                const criterion = this.activeRuleset?.criteria?.find(c => c.id === cid);
                if (criterion?.field === element.id && !criterion.isStub) {
                  isException = true;
                  if (criterion.color) {
                    matchedColor = criterion.color;
                  }
                  break;
                }
              }
            }
            
            // Count hourly exceptions
            let hourlyExceptionCount = 0;
            if (element.section === 'hours' && day.hourCriteriaMap) {
              for (const hourCriteriaIds of Object.values(day.hourCriteriaMap)) {
                if (hourCriteriaIds?.some(cid => element.criteriaIds.includes(cid))) {
                  hourlyExceptionCount++;
                }
              }
            }
            
            data[locKey].elements[element.id].values[day.datetime] = {
              value: day[element.id],
              isException,
              color: matchedColor,
              hourlyExceptionCount,
              isHourly: element.section === 'hours'
            };
          }
        }
      }
      
      return data;
    }
  },

  methods: {
    // ========================================
    // Settings Persistence
    // ========================================

    /**
     * Load settings from localStorage
     */
    loadUserSettings() {
      const settings = loadSettings(this.userId);
      
      this.locations = settings.locations || [];
      this.activeRuleset = settings.activeRuleset || null;
      this.savedRulesets = settings.savedRulesets || [];
      this.startPeriod = settings.dateRangeState?.startPeriod || 'next10days';
      this.endPeriod = settings.dateRangeState?.endPeriod || null;
      this.unitGroup = settings.unitGroup || 'metric';
      // granularity is now auto-derived from criteria
      this.filter = settings.filter || 'all';
      this.pageSize = settings.pageSize || 25;
      this.viewMode = settings.viewMode || 'list';
      this.criteriaExpanded = settings.criteriaExpanded !== false; // default true
    },

    /**
     * Save current settings to localStorage
     */
    saveUserSettings() {
      const settings = {
        locations: this.locations,
        activeRuleset: this.activeRuleset,
        savedRulesets: this.savedRulesets,
        dateRangeState: {
          startPeriod: this.startPeriod,
          endPeriod: this.endPeriod
        },
        unitGroup: this.unitGroup,
        // granularity is auto-derived from criteria, no need to save
        filter: this.filter,
        pageSize: this.pageSize,
        viewMode: this.viewMode,
        criteriaExpanded: this.criteriaExpanded
      };
      saveSettings(this.userId, settings);
    },

    // ========================================
    // Date Range Handling
    // ========================================

    /**
     * Invalidate current results when settings change
     * Shows "Ready to run" screen instead of stale data
     */
    invalidateResults() {
      if (this.hasRun) {
        this.hasRun = false;
        this.locationResults = [];
        this.currentPage = 1;
      }
    },

    /**
     * Handle date range change from DateRangeChooser
     */
    handleDateRangeChange(startPeriod, endPeriod) {
      this.startPeriod = startPeriod;
      this.endPeriod = endPeriod;
      this.invalidateResults();
      this.saveUserSettings();
    },

    /**
     * Resolve date periods to actual ISO date strings
     * Used when building API requests
     */
    resolveDateRange() {
      const [startDate, endDate] = VcDateUtils.resolveDateRange(this.startPeriod, this.endPeriod);
      return {
        startDate: startDate ? VcDateUtils.formatDateIso(startDate) : null,
        endDate: endDate ? VcDateUtils.formatDateIso(endDate) : null
      };
    },

    // ========================================
    // Settings Controls
    // ========================================

    setUnitGroup(newUnitGroup) {
      const oldUnitGroup = this.unitGroup;
      if (oldUnitGroup === newUnitGroup) return;

      // Convert all criteria values in active ruleset
      if (this.activeRuleset) {
        convertRulesetUnits(this.activeRuleset, oldUnitGroup, newUnitGroup);
      }

      // Convert all saved rulesets as well
      if (this.savedRulesets && this.savedRulesets.length > 0) {
        convertAllRulesetsUnits(this.savedRulesets, oldUnitGroup, newUnitGroup);
      }

      this.unitGroup = newUnitGroup;
      this.invalidateResults();
      this.saveUserSettings();
    },

    setFilter(filter) {
      const wasErrors = this.filter === 'errors';
      this.filter = filter;
      this.currentPage = 1;
      
      // Handle view mode for errors tab
      if (filter === 'errors' && this.viewMode !== 'list') {
        // Save current view mode and switch to list for errors
        this.savedViewMode = this.viewMode;
        this.viewMode = 'list';
      } else if (wasErrors && filter !== 'errors' && this.savedViewMode) {
        // Restore saved view mode when leaving errors tab
        this.viewMode = this.savedViewMode;
        this.savedViewMode = null;
      }
      this.saveUserSettings();
    },

    setViewMode(mode) {
      this.viewMode = mode;
      this.saveUserSettings();
    },

    setCombineMode(mode) {
      if (this.activeRuleset) {
        this.activeRuleset.combineMode = mode;
        this.invalidateResults();
        this.saveUserSettings();
      }
    },

    // ========================================
    // UI State
    // ========================================

    toggleCriteriaExpanded() {
      this.criteriaExpanded = !this.criteriaExpanded;
      this.saveUserSettings();
    },

    // ========================================
    // Criteria Management
    // ========================================

    /**
     * Apply a preset ruleset
     */
    applyPreset(preset) {
      this.activeRuleset = preset.factory(this.unitGroup);
      this.showPresetsDropdown = false;
      this.invalidateResults();
      this.saveUserSettings();
    },

    /**
     * Add a new empty criterion
     */
    addCriterion() {
      if (!this.activeRuleset) {
        this.activeRuleset = createRuleset('Custom Rules', [], 'OR');
      }
      
      const criterion = createCriterion('tempmax', 'gt', 30, 'days');
      this.activeRuleset.criteria.push(criterion);
      this.invalidateResults();
      this.saveUserSettings();
    },

    /**
     * Remove a criterion by index
     */
    removeCriterion(index) {
      if (this.activeRuleset) {
        this.activeRuleset.criteria.splice(index, 1);
        this.invalidateResults();
        this.saveUserSettings();
      }
    },

    /**
     * Handle field change in criterion - reset operator, value, and section
     */
    onCriterionFieldChange(criterion) {
      const operators = getOperatorsForField(criterion.field);
      if (operators.length > 0) {
        criterion.operator = operators[0].id;
      }
      
      const fieldType = this.getFieldType(criterion.field);
      if (fieldType === 'numeric') {
        criterion.value = 0;
      } else if (fieldType === 'text') {
        criterion.value = '';
      } else {
        criterion.value = true;
      }
      
      // Update section based on field's available sections
      const field = WEATHER_FIELDS[criterion.field];
      if (field) {
        // Set section to the field's native section, or 'days' for 'both' fields
        criterion.section = field.section === 'both' ? 'days' : field.section;
      }
      
      this.invalidateResults();
      this.saveUserSettings();
    },

    /**
     * Get operators available for a criterion
     */
    getOperatorsForCriterion(criterion) {
      return getOperatorsForField(criterion.field);
    },

    /**
     * Get field type for a criterion
     */
    getFieldType(fieldId) {
      return WEATHER_FIELDS[fieldId]?.type || FIELD_TYPES.NUMERIC;
    },

    /**
     * Get unit string for a field
     */
    getFieldUnit(fieldId) {
      const field = WEATHER_FIELDS[fieldId];
      if (!field || !field.units) return '';
      return field.units[this.unitGroup] || '';
    },

    /**
     * Toggle color picker dropdown for a criterion
     */
    toggleColorPicker(criterionId) {
      if (this.openColorPickerId === criterionId) {
        this.openColorPickerId = null;
      } else {
        this.openColorPickerId = criterionId;
      }
    },

    /**
     * Select a color for a criterion and close the picker
     */
    selectColor(criterion, colorId) {
      criterion.color = colorId;
      this.openColorPickerId = null;
    },

    /**
     * Close color picker when clicking outside
     */
    handleDocumentClick(event) {
      if (this.openColorPickerId && !event.target.closest('.color-picker-dropdown')) {
        this.openColorPickerId = null;
      }
    },

    /**
     * Get hex color for a color ID
     */
    getColorHex(colorId) {
      if (!colorId) return null;
      const color = CRITERION_COLORS.find(c => c.id === colorId);
      return color?.hex || null;
    },

    /**
     * Get Bootstrap class for a color ID
     */
    getColorClass(colorId) {
      if (!colorId) return 'bg-warning text-dark'; // default
      const color = CRITERION_COLORS.find(c => c.id === colorId);
      return color?.class || 'bg-warning text-dark';
    },

    /**
     * Get cell CSS class for grid view based on exception state and color
     */
    getCellClass(cellData) {
      const classes = [];
      if (cellData?.isException && !cellData?.color) {
        classes.push('table-warning');
      }
      if (cellData?.isHourly && cellData?.hourlyExceptionCount > 0) {
        classes.push('cursor-pointer');
      }
      return classes;
    },

    /**
     * Get cell inline style for grid view (custom colors)
     */
    getCellStyle(cellData) {
      const style = {};
      if (cellData?.isHourly && cellData?.hourlyExceptionCount > 0) {
        style.cursor = 'pointer';
      }
      if (cellData?.isException && cellData?.color) {
        const hex = this.getColorHex(cellData.color);
        if (hex) {
          // Use lighter version for background
          style.backgroundColor = hex + '33'; // 20% opacity
        }
      }
      return style;
    },

    /**
     * Check if a field can apply to a given section (days, hours, or both)
     * Based on the field's available data in the API
     */
    canFieldApplyTo(fieldId, targetSection) {
      const field = WEATHER_FIELDS[fieldId];
      if (!field) return false;
      
      const fieldSection = field.section;
      
      // 'both' means available in both days and hours
      if (fieldSection === 'both') return true;
      
      // 'days' fields can only apply to 'days'
      if (fieldSection === 'days') return targetSection === 'days';
      
      // 'hours' fields can only apply to 'hours'
      if (fieldSection === 'hours') return targetSection === 'hours';
      
      return false;
    },

    /**
     * Check if a field's section is locked (only one valid option)
     */
    isFieldSectionLocked(fieldId) {
      const field = WEATHER_FIELDS[fieldId];
      if (!field) return false;
      // If field section is 'days' or 'hours', it can only apply to that section
      return field.section === 'days' || field.section === 'hours';
    },

    /**
     * Get label for a criterion by ID
     * Uses criterion.name if set, otherwise falls back to field label
     */
    getCriterionLabel(criterionId) {
      if (!this.activeRuleset) return criterionId;
      const criterion = this.activeRuleset.criteria.find(c => c.id === criterionId);
      if (!criterion) return criterionId;
      // Use custom name if set, otherwise use field label
      if (criterion.name) {
        return criterion.name;
      }
      const field = WEATHER_FIELDS[criterion.field];
      return field?.label || criterion.field;
    },

    /**
     * Get a detailed description of a criterion for tooltips
     */
    getCriterionDescription(criterionId) {
      if (!this.activeRuleset) return '';
      const criterion = this.activeRuleset.criteria.find(c => c.id === criterionId);
      if (!criterion) return '';
      
      const field = WEATHER_FIELDS[criterion.field];
      const fieldLabel = field?.label || criterion.field;
      // OPERATORS is an object with keys like GT, GTE, etc. - find by operator id
      const operatorEntry = Object.values(OPERATORS).find(o => o.id === criterion.operator);
      const operatorLabel = operatorEntry?.label || criterion.operator;
      
      // Format value with unit if applicable
      let valueStr = String(criterion.value);
      if (field?.unit) {
        valueStr += ' ' + field.unit;
      }
      
      // Build description: "Max Temperature > 30°C"
      let description = `${fieldLabel} ${operatorLabel} ${valueStr}`;
      
      // Add custom name if set
      if (criterion.name) {
        description = `${criterion.name}: ${description}`;
      }
      
      return description;
    },

    // ========================================
    // Locations Management
    // ========================================

    /**
     * Handle locations changed from LocationsEditor
     */
    handleLocationsChanged(newLocations) {
      this.locations = newLocations;
      this.invalidateResults();
      this.saveUserSettings();
    },

    // ========================================
    // API Request Handling
    // ========================================

    /**
     * Main function to run the exception check
     */
    async runExceptionCheck() {
      if (!this.canRun) return;

      this.isRunning = true;
      this.hasRun = true;
      this.locationResults = [];
      this.currentPage = 1;
      this.requestQueue = [];
      this.activeRequests = 0;

      // Resolve date range
      const { startDate, endDate } = this.resolveDateRange();

      // Initialize results with pending status
      // Invalid locations (no location string) are marked as error immediately
      for (const loc of this.locations) {
        const isInvalid = !loc.location || loc.location.trim() === '';
        
        this.locationResults.push({
          id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36),
          location: loc.location || '',
          name: loc.name || null,
          status: isInvalid ? 'error' : 'pending',
          errorMessage: isInvalid ? 'Invalid location - empty or missing' : null,
          cached: false,
          nextExceptionDate: null,
          exceptionCount: 0,
          triggeredCriteria: [],
          days: [],
          timezone: null,
          weatherData: null
        });
      }

      // Queue valid locations for fetching
      for (let i = 0; i < this.locationResults.length; i++) {
        const result = this.locationResults[i];
        if (result.status === 'pending') {
          this.requestQueue.push({
            index: i,
            location: result.location,
            name: result.name,
            startDate,
            endDate
          });
        }
      }

      // Start processing queue
      this.processRequestQueue();
    },

    /**
     * Process the request queue with concurrency limit
     */
    processRequestQueue() {
      while (this.activeRequests < this.MAX_CONCURRENT_REQUESTS && this.requestQueue.length > 0) {
        const request = this.requestQueue.shift();
        this.activeRequests++;
        this.fetchLocationWeather(request)
          .finally(() => {
            this.activeRequests--;
            this.processRequestQueue();
          });
      }

      // Check if all done
      if (this.activeRequests === 0 && this.requestQueue.length === 0) {
        this.isRunning = false;
      }
    },

    /**
     * Fetch weather data for a single location
     * 
     * Timeline API Request Formation:
     * - Endpoint: getServerRoot(serverRootOverride) + /VisualCrossingWebServices/rest/services/timeline/{location}/{startDate}/{endDate}
     * - Method: POST with JSON body
     * - Required parameters:
     *   - key: API key from session
     *   - unitGroup: 'us' or 'metric'
     *   - elements: comma-separated list of required fields
     *   - include: 'days,alerts' or 'days,hours,alerts' based on granularity
     */
    async fetchLocationWeather(request) {
      const { index, location, startDate, endDate } = request;
      const result = this.locationResults[index];

      // Check cache first
      const cacheKey = generateCacheKey(
        this.userId,
        location,
        startDate,
        endDate,
        this.unitGroup,
        this.derivedGranularity
      );

      const cached = getCacheEntry(cacheKey);
      if (cached) {
        // Use cached data with a small delay to let UI update
        // This prevents UI blocking when many locations are cached
        await new Promise(resolve => setTimeout(resolve, 0));
        this.processWeatherData(index, cached, true);
        return;
      }

      // Build API URL
      // Location is URL-encoded for the path segment
      const encodedLocation = encodeURIComponent(location);
      const baseUrl = getServerRoot(this.serverRootOverride);
      let url = `${baseUrl}/VisualCrossingWebServices/rest/services/timeline/${encodedLocation}`;
      
      if (startDate) {
        url += `/${startDate}`;
        if (endDate) {
          url += `/${endDate}`;
        }
      }

      // Build POST body
      // Elements: required fields for exception evaluation
      // Include: data sections to request (always include alerts)
      const postData = {
        key: this.apiKey,
        unitGroup: this.unitGroup,
        elements: 'tempmax,tempmin,temp,precip,precipprob,windspeed,windgust,snow,snowdepth,conditions,datetime',
        include: this.derivedGranularity === 'days+hours' ? 'days,hours,alerts' : 'days,alerts',
        iconSet: 'icons2'
      };

      try {
        const response = await fetch(url, {
          method: 'POST',
          mode: 'cors',
          cache: 'no-cache',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json'
          },
          redirect: 'error',
          referrerPolicy: 'strict-origin-when-cross-origin',
          body: JSON.stringify(postData)
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `HTTP ${response.status}`);
        }

        const data = await response.json();

        // Cache the successful response
        setCacheEntry(cacheKey, data);

        // Process the data
        this.processWeatherData(index, data, false);

      } catch (error) {
        // Handle error
        result.status = 'error';
        result.errorMessage = error.message || 'Failed to fetch weather data';
      }
    },

    /**
     * Process weather data and evaluate exceptions
     */
    processWeatherData(index, data, fromCache) {
      const result = this.locationResults[index];
      
      if (!data || !data.days) {
        result.status = 'error';
        result.errorMessage = 'Invalid response data';
        return;
      }

      result.status = 'ok';
      result.cached = fromCache;
      
      // Store timezone from API response
      // Timezone handling: use location's timezone as returned by API
      // If missing, the display will use browser's local timezone
      result.timezone = data.timezone || null;

      // Evaluate exceptions using the active ruleset
      const includeHours = this.derivedGranularity === 'days+hours';
      const evaluation = evaluateRulesetForLocation(this.activeRuleset, data, includeHours);

      result.exceptionCount = evaluation.exceptionCount;
      result.nextExceptionDate = evaluation.nextExceptionDate;
      
      // Collect all unique triggered criteria
      const triggeredSet = new Set();
      evaluation.exceptionDays.forEach(ed => {
        ed.matchedCriteria.forEach(cid => triggeredSet.add(cid));
      });
      result.triggeredCriteria = Array.from(triggeredSet);

      // Store location-level alerts for reference
      const locationAlerts = Array.isArray(data.alerts) ? data.alerts : [];
      result.alertsData = locationAlerts.length > 0 ? locationAlerts : null;

      // Store processed day data for detail view
      result.days = data.days.map(day => {
        const dayEval = evaluation.exceptionDays.find(ed => ed.date === day.datetime);
        // Check if any alerts are active for this specific day based on onset/ends times
        const alertInfo = getActiveAlertsForDay(locationAlerts, day.datetime);
        return {
          datetime: day.datetime,
          // Store all numeric fields for grid view
          tempmax: day.tempmax,
          tempmin: day.tempmin,
          temp: day.temp,
          precip: day.precip,
          precipprob: day.precipprob,
          windspeed: day.windspeed,
          windgust: day.windgust,
          snow: day.snow,
          snowdepth: day.snowdepth,
          conditions: day.conditions,
          // Use datetime-aware alert checking based on onset/ends
          hasAlerts: alertInfo.hasActiveAlert,
          activeAlerts: alertInfo.matchingAlerts,
          alertMatchedHours: dayEval?.alertMatchedHours || [],
          matchedHours: dayEval?.matchedHours || [],
          matchedCriteria: dayEval?.matchedCriteria || [],
          hourCriteriaMap: dayEval?.hourCriteriaMap || {},
          // Store hourly data for expandable view
          hours: day.hours || null,
          isException: !!dayEval,
          isExpanded: false
        };
      });

      // Store raw data for potential re-evaluation
      result.weatherData = data;
    },

    // ========================================
    // UI Helpers
    // ========================================

    /**
     * Get row class based on result status
     * - Error: red background
     * - OK with matches: light warning background
     * - OK no matches: default
     */
    getRowClass(result) {
      if (result.status === 'error') {
        return 'table-danger';
      }
      if (result.status === 'ok' && result.exceptionCount > 0) {
        return 'table-warning';
      }
      return '';
    },

    /**
     * Show location detail modal
     */
    showLocationDetail(result) {
      if (result.status !== 'pending') {
        this.selectedResult = result;
      }
    },

    /**
     * Open detail modal from grid cell click, with specific day expanded
     */
    openGridCellDetail(locationStr, date) {
      const result = this.locationResults.find(r => r.location === locationStr);
      if (result && result.status === 'ok') {
        // Expand the specific day
        if (result.days) {
          for (const day of result.days) {
            day.isExpanded = day.datetime === date;
          }
        }
        this.selectedResult = result;
      }
    },

    /**
     * Format value for display in detail table
     */
    formatValue(value, type) {
      if (value === null || value === undefined) return '-';

      switch (type) {
        case 'temp':
          return `${Math.round(value)}${this.unitGroup === 'us' ? '°F' : '°C'}`;
        case 'precip':
          return `${Number(value).toFixed(1)}${this.unitGroup === 'us' ? ' in' : ' mm'}`;
        case 'speed':
          return `${Math.round(value)}${this.unitGroup === 'us' ? ' mph' : ' km/h'}`;
        case 'snow':
          return `${Number(value).toFixed(1)}${this.unitGroup === 'us' ? ' in' : ' cm'}`;
        case 'percent':
          return `${Math.round(value)}%`;
        default:
          return value;
      }
    },

    /**
     * Format value for grid view (just the number, no unit)
     */
    formatGridValue(value, fieldId) {
      if (value === null || value === undefined) return '-';
      
      const field = WEATHER_FIELDS[fieldId];
      if (!field) return value;
      
      // Temperature and speed should be whole numbers
      if (['tempmax', 'tempmin', 'temp', 'windspeed', 'windgust'].includes(fieldId)) {
        return Math.round(value);
      }
      
      // Precipitation, snow - 1-3 decimal places depending on value
      if (['precip', 'snow', 'snowdepth'].includes(fieldId)) {
        if (value === 0) return '0';
        if (value < 0.01) return value.toFixed(3);
        if (value < 0.1) return value.toFixed(2);
        return Number(value).toFixed(1);
      }
      
      // Percent
      if (fieldId === 'precipprob') {
        return Math.round(value);
      }
      
      return value;
    },

    /**
     * Format date for grid view column headers (M/D format)
     */
    formatGridDate(datetime) {
      if (!datetime) return '';
      const parts = datetime.split('-');
      if (parts.length >= 3) {
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);
        return `${month}/${day}`;
      }
      return datetime;
    },

    /**
     * Format tooltip text for alert badges showing event names and hours
     */
    formatAlertTooltip(alerts, matchedHours) {
      if (!alerts || alerts.length === 0) return '';
      
      const parts = [];
      
      // List alert event types
      const events = alerts.map(a => a.event || 'Weather Alert').join(', ');
      parts.push(events);
      
      // Show matched hours if available
      if (matchedHours && matchedHours.length > 0) {
        parts.push(`Hours: ${matchedHours.join(', ')}`);
      }
      
      // Show onset/ends if available
      if (alerts.length === 1) {
        const alert = alerts[0];
        if (alert.onset || alert.ends) {
          const onset = alert.onset ? new Date(alert.onset).toLocaleString() : 'N/A';
          const ends = alert.ends ? new Date(alert.ends).toLocaleString() : 'N/A';
          parts.push(`From: ${onset} To: ${ends}`);
        }
      }
      
      return parts.join('\n');
    },

    /**
     * Go to a specific page
     */
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    // ========================================
    // Cache Management
    // ========================================

    clearCache() {
      clearAllCache();
      if (window.VcCommonModals?.showInfo) {
        window.VcCommonModals.showInfo('Cache Cleared', 'Weather data cache has been cleared.');
      }
    },

    // ========================================
    // Import/Export
    // ========================================

    exportConfig() {
      const settings = {
        locations: this.locations,
        activeRuleset: this.activeRuleset,
        savedRulesets: this.savedRulesets,
        dateRangeState: {
          startPeriod: this.startPeriod,
          endPeriod: this.endPeriod
        },
        unitGroup: this.unitGroup,
        // granularity is auto-derived from criteria
        filter: this.filter,
        pageSize: this.pageSize,
        viewMode: this.viewMode,
        criteriaExpanded: this.criteriaExpanded
      };
      exportSettings(settings);
    },

    async handleImportFile(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      this.importError = null;
      this.importSuccess = false;

      const result = await importSettingsFromFile(file);
      
      if (result.success) {
        // Apply imported settings
        const settings = result.settings;
        this.locations = settings.locations || [];
        this.activeRuleset = settings.activeRuleset || null;
        this.savedRulesets = settings.savedRulesets || [];
        this.startPeriod = settings.dateRangeState?.startPeriod || 'next10days';
        this.endPeriod = settings.dateRangeState?.endPeriod || null;
        this.unitGroup = settings.unitGroup || 'metric';
        // granularity is auto-derived from criteria
        this.filter = settings.filter || 'all';
        this.pageSize = settings.pageSize || 25;
        this.viewMode = settings.viewMode || 'list';
        this.criteriaExpanded = settings.criteriaExpanded !== false;

        this.saveUserSettings();
        this.importSuccess = true;
        
        // Clear file input
        if (this.$refs.importFileInput) {
          this.$refs.importFileInput.value = '';
        }
      } else {
        this.importError = result.error;
      }
    }
  },

  watch: {
    // Watch for session changes to reload settings
    sessionId(newVal) {
      if (newVal) {
        this.loadUserSettings();
      }
    },
    
    // Watch for criteria changes (values, operators, sections, enabled state via v-model)
    // This catches changes not handled by explicit methods
    activeRuleset: {
      handler(newVal, oldVal) {
        // Invalidate results, don't save (explicit methods handle saving)
        // Skip if this is initial load (oldVal is null/undefined)
        if (this.hasRun && oldVal != null) {
          this.invalidateResults();
        }
      },
      deep: true
    }
  },

  mounted() {
    /**
     * Session/Auth Initialization
     * 
     * Uses window.vcCommunicator to:
     * 1. Get initial session state if already logged in
     * 2. Listen for 'createsession' events when user logs in
     * 3. Listen for 'terminatesession' events when user logs out
     * 
     * The sessionState object contains:
     * - apiKey: Used for Timeline API requests
     * - userId: Used for scoping localStorage
     * - subscriptionParams: User's subscription details
     */
    const vcCommunicator = window.vcCommunicator;
    
    if (!vcCommunicator) {
      console.warn('WeatherExceptionTracker: vcCommunicator not found');
      return;
    }

    // Get initial session state
    this.sessionId = vcCommunicator.sessionId;
    this.sessionState = vcCommunicator.sessionState;
    this.isContainerInited = vcCommunicator.isInited || this.sessionId;

    // Load settings if we have a session
    if (this.sessionId) {
      this.loadUserSettings();
    }

    // Listen for session creation
    vcCommunicator.addHandler('createsession', (sessionId, sessionState) => {
      this.sessionId = sessionId;
      this.sessionState = sessionState;
      this.isContainerInited = true;
      this.loadUserSettings();
    });

    // Listen for session termination
    vcCommunicator.addHandler('terminatesession', () => {
      this.sessionId = null;
      this.sessionState = null;
      // Reset to defaults
      this.locations = [];
      this.activeRuleset = null;
      this.savedRulesets = [];
      this.locationResults = [];
      this.hasRun = false;
    });

    // Handle click outside for dropdown
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.dropdown')) {
        this.showPresetsDropdown = false;
      }
      // Close color picker when clicking outside
      if (this.openColorPickerId && !e.target.closest('.color-picker-dropdown')) {
        this.openColorPickerId = null;
      }
    });
  },

  beforeUnmount() {
    // Save settings before unmounting
    if (this.hasSession) {
      this.saveUserSettings();
    }
  }
};
</script>

<style scoped>
/* Custom background colors for non-Bootstrap colors */
.bg-purple { background-color: #6f42c1 !important; color: white; }
.bg-orange { background-color: #fd7e14 !important; color: white; }
.bg-pink { background-color: #d63384 !important; color: white; }
.bg-teal { background-color: #20c997 !important; color: white; }

/* Color picker dropdown styles */
.color-option {
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.color-option:hover:not(.bg-primary) {
  background-color: #f8f9fa;
}

/* Modal styles for proper display without Bootstrap JS */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1055;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  outline: 0;
}

.modal.show {
  display: block !important;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1050;
  width: 100vw;
  height: 100vh;
  background-color: #000;
}

.modal-backdrop.show {
  opacity: 0.5;
}

.modal-dialog {
  position: relative;
  z-index: 1060;
}

/* Dropdown positioning */
.dropdown-menu.show {
  display: block;
}

/* Criteria list styling */
.criteria-list .form-control,
.criteria-list .form-select {
  font-size: 0.875rem;
}

/* Table improvements */
.table-responsive {
  font-size: 0.875rem;
}

.table th {
  white-space: nowrap;
}

/* Sticky header for detail modal table */
.sticky-top {
  background-color: #f8f9fa;
}

/* Grid view styles */
.grid-table {
  font-size: 0.8rem;
}

.grid-table th,
.grid-table td {
  padding: 0.25rem 0.5rem;
  white-space: nowrap;
}

.grid-table .sticky-col {
  position: sticky;
  left: 0;
  background-color: #fff;
  z-index: 1;
  border-right: 2px solid #dee2e6;
}

.grid-table thead .sticky-col {
  background-color: #f8f9fa;
  z-index: 2;
}

.grid-table .location-name {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.grid-table .exception-cell {
  background-color: #fff3cd !important;
  font-weight: 500;
}

.grid-table tbody tr:hover .sticky-col {
  background-color: #f8f9fa;
}
</style>
